import Chip from "@mui/material/Chip";
import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import {
  EditPurchaseButton,
  RenewSubscriptionChip,
  TerminatePurchaseButton,
} from "../domains";
import type { ProductPurchaseRow } from "./types";

const typeLabel: Record<string, string> = {
  PPV: "Разовая покупка",
  SUBSCRIPTION: "Подписка",
};

function renderType(type: string) {
  return typeLabel[type] ?? type;
}

const isExpired = (expiredAt: string) =>
  new Date(expiredAt).getTime() <= Date.now();

function renderBuyer(purchase: ProductPurchaseRow) {
  const userId = purchase.payment?.userId;
  if (userId) {
    return (
      <CrudLink path={PagePaths.users} tab="details" id={userId}>
        {purchase.buyerEmail ?? "—"}
      </CrudLink>
    );
  }
  return purchase.buyerEmail ?? "—";
}

export const getColumnsConfig = (): ColumnDefinition<ProductPurchaseRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "type",
    label: "Тип",
    options: {
      customBodyRender: (value: unknown) => renderType(value as string),
    },
  },
  {
    name: "amount",
    label: "Сумма",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const purchase = data as ProductPurchaseRow;
        if (!purchase.payment) return "—";
        return `${purchase.payment.amount.value} ${purchase.payment.amount.currency}`;
      },
    },
  },
  {
    name: "buyer",
    label: "Покупатель",
    options: {
      sort: false,
      customBodyRender: (data: unknown) =>
        renderBuyer(data as ProductPurchaseRow),
    },
  },
  {
    name: "renew",
    label: "Автопродление",
    options: {
      sort: false,
      // У разовой покупки подписки нет, продлевать нечего.
      customBodyRender: (data: unknown) => {
        const purchase = data as ProductPurchaseRow;
        if (!purchase.subscription) return null;
        return (
          <RenewSubscriptionChip
            id={purchase.id}
            renewSubscription={purchase.subscription.renewSubscription}
            autoRenewEditable={purchase.subscription.autoRenewEditable}
            expired={isExpired(purchase.expiredAt as string)}
            pastDue={purchase.subscription.status === "PAST_DUE"}
          />
        );
      },
    },
  },
  {
    name: "product",
    label: "Продукт",
    options: {
      sort: false,
      // Как в легаси (minWidth 300): иначе длинные названия событий
      // переносятся по слову на строку.
      setCellHeaderProps: () => ({ style: { minWidth: 280 } }),
      // Продукты admin-only не редактируются (нет админ-поверхности
      // управления продуктами) — ссылки нет, только текст.
      customBodyRender: (data: unknown) => {
        const purchase = data as ProductPurchaseRow;
        return purchase.product?.title ?? "—";
      },
    },
  },
  {
    name: "startAt",
    label: "Начало",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "expiredAt",
    label: "Окончание",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "refunded",
    label: "Возврат",
    options: {
      customBodyRender: (value: unknown) => {
        const refunded = value as boolean;
        return (
          <Chip
            size="small"
            color={refunded ? "warning" : "default"}
            label={refunded ? "Да" : "Нет"}
          />
        );
      },
    },
  },
  {
    name: "disputed",
    label: "Диспут",
    options: {
      customBodyRender: (value: unknown) => {
        const disputed = value as boolean;
        return (
          <Chip
            size="small"
            color={disputed ? "error" : "default"}
            label={disputed ? "Да" : "Нет"}
          />
        );
      },
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const purchase = data as ProductPurchaseRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={purchase.organizationId}
          >
            {purchase.organization?.title ?? "—"}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "actions",
    label: "",
    options: {
      sort: false,
      setCellHeaderProps: () => ({ style: { width: 100 } }),
      customBodyRender: (data: unknown) => {
        const purchase = data as ProductPurchaseRow;
        return (
          <Stack direction="row" spacing={0.5}>
            <TerminatePurchaseButton
              id={purchase.id}
              expiredAt={purchase.expiredAt as string}
            />
            <EditPurchaseButton
              id={purchase.id}
              startAt={purchase.startAt as string}
              expiredAt={purchase.expiredAt as string}
            />
          </Stack>
        );
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Покупки",
  refetchList: "Обновить список",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<ProductPurchaseRow> => ({
    main: {
      title: "Покупка",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap>{renderType(row.type)}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.organization?.title ?? "—"}
          </Typography>
        </Stack>
      ),
    },
    startAt: {
      title: "Начало",
      sx: { width: 132, flexShrink: 0 },
      renderCell: (row) => <CustomCellDate date={row.startAt as string} />,
    },
  });
